import {
  propertySearch,
  PropertySearchReqTypeBody,
  PropertySearchResTypeData,
} from '../services/api';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useGetCurrentUser } from './auth.ts';
import { AuthUser } from 'aws-amplify/auth';
import { useMemo } from 'react';
import { SEARCH_LIMIT } from '../constants/search.ts';

const isSearchPermitted = (
  user: AuthUser | undefined | null,
  count = 0,
  loading?: boolean
) => {
  if (loading) return true;
  if (user?.userId) return true;
  if (!user?.userId) {
    return count < 10;
  }
};

// Note: This is not a replacement for server-side rate limiting, however, it's
// meant to be a more restrictive rate limit for unauthenticated users. By splitting client and server rate limiting,
// we can allow rooms of unauthenticated people with the same IP address to search without hitting the server rate limit,
// which is in place to prevent abuse (like botting) from a single IP address.
const useGetSearchCount = () => {
  const queryClient = useQueryClient();
  return useQuery<number>({
    queryKey: ['search-count'],
    meta: { persist: true },
    queryFn: () => {
      const count = queryClient.getQueryData<number>(['search-count']);
      return count || 0;
    },
  });
};

class SearchLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SearchLimitError';
  }
}

interface UseGetLandSearchProps {
  query: Omit<PropertySearchReqTypeBody, 'resultIndex' | 'size'> | null;
  setPage?: (page: number) => void;
  initialPage?: number;
}

export const useGetLandSearchSingle = (
  query: UseGetLandSearchProps['query']
) => {
  return useQuery({
    queryKey: ['search', 'single', query],
    queryFn: async ({ signal }) => {
      return propertySearch({ body: query || {}, signal });
    },
  });
};

export const useGetLandSearch = ({
  query: baseQuery,
  setPage,
  initialPage = 1,
}: UseGetLandSearchProps) => {
  const queryClient = useQueryClient();
  const { data: user, isPending: loading } = useGetCurrentUser();
  const { data: count } = useGetSearchCount();

  const { isPending, error, data, refetch, fetchNextPage, fetchPreviousPage } =
    useInfiniteQuery({
      queryKey: ['search', baseQuery],
      enabled: !!baseQuery && !loading,
      retry: (failureCount, error) =>
        error instanceof SearchLimitError ? false : failureCount < 3,
      queryFn: async ({ signal, pageParam }) => {
        if (setPage) {
          setPage(pageParam);
        }

        if (!isSearchPermitted(user, count, loading)) {
          throw new SearchLimitError('You have reached the search limit.');
        }

        // increment the search count limit if not logged in.
        if (!user?.userId && count !== undefined) {
          queryClient.setQueryData(['search-count'], count + 1);
        }

        // TODO: Remove this, it's brittle and a bad practice
        // Sets query for sharing/saving searches
        queryClient.setQueryData<PropertySearchReqTypeBody>(
          ['query'],
          baseQuery || {}
        );

        const query = {
          ...baseQuery,
          size: SEARCH_LIMIT,
          pageIndex: pageParam * SEARCH_LIMIT,
        };

        return propertySearch({ body: query || {}, signal });
      },
      getNextPageParam: (_, __, lastPageParam) => lastPageParam + 1,
      getPreviousPageParam: (_, __, lastPageParam) => lastPageParam - 1,
      // Get from `query`
      initialPageParam: initialPage,
    });

  const isOverLimit = useMemo(() => error instanceof SearchLimitError, [error]);

  const mergedData = useMemo(() => {
    if (!data) return data;
    return data.pages.reduce(
      (prev, response) => {
        prev.data = prev.data.concat(response.data);
        return prev;
      },
      {
        data: [] as PropertySearchResTypeData[],
        count: data.pages[0].recordCount,
        searchType: data.pages[0].searchType,
      }
    );
  }, [data]);

  return {
    loading: isPending,
    error,
    data: mergedData,
    refetch,
    isOverLimit,
    fetchNextPage,
    fetchPreviousPage,
  };
};
